import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import {
  LocalStorageService,
  reportDtCustomError,
  useHTTPError,
} from 'br2s-ui-common-utils-02';
import { useStore } from '@/redux/store';
import { AuthAPI } from '@/services/api/auth';

type SessionManagerProps = {
  children: React.ReactNode | React.ReactNode[];
};

const SessionManager = ({ children }: SessionManagerProps) => {
  const [cookies, setCookie] = useCookies(['isSessionActive', 'sessionType']);
  const navigate = useNavigate();
  const {
    authenticate,
    logoff,
    auth: { authenticated },
  } = useStore();
  const { handleHTTPError } = useHTTPError();

  const skipSessionCall = authenticated || cookies.sessionType === 'public';

  // istanbul ignore next
  useEffect(() => {
    const checkTokenExpired = () => {
      if (!LocalStorageService.hasAuth()) {
        generateSession();
        return;
      }
    };

    document.addEventListener('concurrency-error', clearSession);
    window.addEventListener('refresh_token_counter_expire', checkTokenExpired);

    return () => {
      document.removeEventListener('concurrency-error', clearSession);
      window.removeEventListener(
        'refresh_token_counter_expire',
        checkTokenExpired,
      );
    };
  }, []);

  useEffect(() => {
    if (LocalStorageService.hasAuth() && !authenticated) {
      // istanbul ignore next
      if (cookies.isSessionActive) {
        authenticate();
        return;
      }

      clearSession();
    }

    if (!skipSessionCall) {
      generateSession();
    }
  }, [authenticated, skipSessionCall]);

  const generateSession = async () => {
    try {
      const response = await AuthAPI.getSessionId();
      if (response?.status === 200) {
        setCookie('sessionType', 'public', {
          path: '/',
        });
      }
    } catch (error) {
      console.error(error);
      reportDtCustomError('GenericHandledError', JSON.stringify(error));

      // istanbul ignore next
      handleHTTPError(error, { errorType: 'inline' });
    }
  };

  /**
   * reset Cart item counts to 0 when user logout
   */
  const handleTriggerEventForSetLatestCartCount = (): void => {
    document.dispatchEvent(
      new CustomEvent('ips-cart-count', {
        detail: {
          cart: {
            productCount: 0,
            subscriptionCount: 0,
          },
        },
      }),
    );
  };

  const clearSession = () => {
    localStorage.clear();
    localStorage.setItem('mockAuth', 'true');
    handleTriggerEventForSetLatestCartCount();
    logoff();
    navigate('/');
  };

  return children as JSX.Element;
};

export default SessionManager;
